import { apiRequest } from "./api";

export type StorePaymentType = "SALARY" | "COMMISSION";

export interface Store {
  storeId: string;
  businessId: string;
  name: string;
  address: string;
  paymentType: StorePaymentType;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface StoreService {
  serviceId: string;
  storeId: string;
  name: string;
  serviceAmount?: number;
  commissionRate?: number;
  commissionType?: "percentage" | "fixed";
  commissionFixed?: number;
  lockAmount?: boolean;
  createdBy: string;
  createdAt: string;
  updatedAt: string;
}

export interface StoreRecord {
  recordId: string;
  storeId: string;
  date: string;
  serviceName: string;
  serviceId?: string;
  price: number;
  staffId: string;
  staffName: string;
  notes?: string;
  createdBy: string;
  createdAt: string;
  walletTxnId?: string;
  walletTxnIds?: string[];
  additionalStaff?: { staffId: string; staffName: string }[];
}

export interface StoreExpense {
  expenseId: string;
  storeId: string;
  businessId: string;
  date: string;
  description: string;
  amount: number;
  category?: string;
  createdBy: string;
  createdAt: string;
}

export interface StoreIncome {
  incomeId: string;
  storeId: string;
  businessId: string;
  date: string;
  description: string;
  amount: number;
  category?: string;
  createdBy: string;
  createdAt: string;
}

export interface StoreStaff {
  userId: string;
  firstName: string;
  lastName: string;
  position: string;
  isStoreManager: boolean;
}

export interface StaffWalletBalance {
  userId: string;
  name: string;
  totalEarned: number;
  availableBalance: number;
}

export interface StoreConfigCategory {
  id: string;
  name: string;
  enabled: boolean;
}

export interface StoreConfig {
  expenseCategories: StoreConfigCategory[];
  incomeCategories: StoreConfigCategory[];
  updatedAt?: string;
  updatedBy?: string;
}

export interface SaveStoreConfigPayload {
  storeId: string;
  expenseCategories: StoreConfigCategory[];
  incomeCategories: StoreConfigCategory[];
}

export interface CreateStorePayload { businessId: string; name: string; address: string; paymentType?: StorePaymentType; }
export interface UpdateStorePayload { name?: string; address?: string; paymentType?: StorePaymentType; }
export interface CreateStoreServicePayload { storeId: string; name: string; serviceAmount?: number; commissionRate?: number; commissionType?: "percentage" | "fixed"; commissionFixed?: number; lockAmount?: boolean; }
export interface UpdateStoreServicePayload { storeId: string; name: string; serviceAmount?: number; commissionRate?: number; commissionType?: "percentage" | "fixed"; commissionFixed?: number; lockAmount?: boolean; }
export interface CreateStoreRecordPayload {
  storeId: string;
  date: string;
  serviceName: string;
  serviceId?: string;
  price: number;
  staffId: string;
  staffName: string;
  additionalStaff?: { staffId: string; staffName: string }[];
  notes?: string;
}

export interface CreateStoreExpensePayload {
  storeId: string;
  date: string;
  description: string;
  amount: number;
  category?: string;
}

export interface UpdateStoreExpensePayload {
  storeId: string;
  date: string;
  description?: string;
  amount?: number;
  category?: string;
}

export interface CreateStoreIncomePayload {
  storeId: string;
  date: string;
  description: string;
  amount: number;
  category?: string;
}

export interface UpdateStoreIncomePayload {
  storeId: string;
  date: string;
  description?: string;
  amount?: number;
  category?: string;
}
export interface UpdateStoreRecordPayload {
  storeId: string;
  date: string;
  price?: number;
  staffId?: string;
  staffName?: string;
  additionalStaff?: { staffId: string; staffName: string }[];
  notes?: string;
}

export const storeApi = {
  // Stores
  createStore: (payload: CreateStorePayload) =>
    apiRequest<Store>("/store/stores", { method: "POST", body: payload }),
  listStores: (businessId?: string | null) =>
    apiRequest<{ items: Store[] }>("/store/stores", {
      params: businessId ? { businessId } : undefined,
    }),
  updateStore: (storeId: string, payload: UpdateStorePayload) =>
    apiRequest<Store>(`/store/stores/${storeId}`, { method: "PATCH", body: payload }),

  // Services
  createService: (payload: CreateStoreServicePayload) =>
    apiRequest<StoreService>("/store/services", { method: "POST", body: payload }),
  listServices: (storeId: string) =>
    apiRequest<{ items: StoreService[] }>("/store/services", { params: { storeId } }),
  updateService: (serviceId: string, payload: UpdateStoreServicePayload) =>
    apiRequest<StoreService>(`/store/services/${serviceId}`, { method: "PATCH", body: payload }),
  deleteService: (serviceId: string, storeId: string) =>
    apiRequest<{ deleted: boolean }>(`/store/services/${serviceId}`, {
      method: "DELETE",
      params: { storeId },
    }),

  // Records
  createRecord: (payload: CreateStoreRecordPayload) =>
    apiRequest<StoreRecord>("/store/records", { method: "POST", body: payload }),
  updateRecord: (recordId: string, payload: UpdateStoreRecordPayload) =>
    apiRequest<StoreRecord>(`/store/records/${recordId}`, {
      method: "PATCH",
      body: payload,
      params: { storeId: payload.storeId, date: payload.date },
    }),
  listRecords: (
    storeId: string,
    prefix: string,
    opts: { staffId?: string; serviceId?: string; createdBy?: string; limit?: number; lastKey?: string } = {},
  ) =>
    apiRequest<{ items: StoreRecord[]; nextKey?: string; totalPrice?: number; totalCount?: number }>("/store/records", {
      params: {
        storeId,
        prefix,
        ...(opts.staffId ? { staffId: opts.staffId } : {}),
        ...(opts.serviceId ? { serviceId: opts.serviceId } : {}),
        ...(opts.createdBy ? { createdBy: opts.createdBy } : {}),
        ...(opts.limit !== undefined ? { limit: String(opts.limit) } : {}),
        ...(opts.lastKey ? { lastKey: opts.lastKey } : {}),
      },
    }),

  deleteRecord: (recordId: string, storeId: string, date: string) =>
    apiRequest<{ deleted: boolean }>(`/store/records/${recordId}`, {
      method: "DELETE",
      params: { storeId, date },
    }),

  async exportRecords(
    storeId: string,
    prefix: string,
    opts: { serviceId?: string } = {},
  ): Promise<StoreRecord[]> {
    const all: StoreRecord[] = [];
    let lastKey: string | undefined;
    do {
      const res = await storeApi.listRecords(storeId, prefix, { ...opts, limit: 200, lastKey });
      all.push(...res.items);
      lastKey = res.nextKey;
    } while (lastKey);
    return all;
  },

  // Staff
  listStaff: (storeId: string) =>
    apiRequest<{ items: StoreStaff[] }>("/store/staff", { params: { storeId } }),
  listStaffWalletBalances: (storeId: string) =>
    apiRequest<{ items: StaffWalletBalance[] }>("/store/wallet-balances", { params: { storeId } }),
  assignStaff: (storeId: string, userId: string) =>
    apiRequest<{ success: boolean }>(`/store/stores/${storeId}/staff`, {
      method: "POST",
      body: { userId },
    }),
  removeStaff: (storeId: string, userId: string) =>
    apiRequest<{ success: boolean }>(`/store/stores/${storeId}/staff/${userId}`, {
      method: "DELETE",
    }),

  // Expenses
  createExpense: (payload: CreateStoreExpensePayload) =>
    apiRequest<StoreExpense>("/store/expenses", { method: "POST", body: payload }),
  listExpenses: (storeId: string, prefix?: string, opts: { category?: string; limit?: number; lastKey?: string } = {}) =>
    apiRequest<{ items: StoreExpense[]; nextKey?: string; totalAmount?: number; totalCount?: number }>("/store/expenses", {
      params: {
        storeId,
        ...(prefix ? { prefix } : {}),
        ...(opts.category ? { category: opts.category } : {}),
        ...(opts.limit !== undefined ? { limit: String(opts.limit) } : {}),
        ...(opts.lastKey ? { lastKey: opts.lastKey } : {}),
      },
    }),
  async exportExpenses(storeId: string, prefix?: string, opts: { category?: string } = {}): Promise<StoreExpense[]> {
    const all: StoreExpense[] = [];
    let lastKey: string | undefined;
    do {
      const res = await storeApi.listExpenses(storeId, prefix, { ...opts, limit: 200, lastKey });
      all.push(...res.items);
      lastKey = res.nextKey;
    } while (lastKey);
    return all;
  },
  updateExpense: (expenseId: string, payload: UpdateStoreExpensePayload) =>
    apiRequest<StoreExpense>(`/store/expenses/${expenseId}`, {
      method: "PATCH",
      body: payload,
      params: { storeId: payload.storeId },
    }),
  deleteExpense: (expenseId: string, storeId: string, date: string) =>
    apiRequest<{ deleted: boolean }>(`/store/expenses/${expenseId}`, {
      method: "DELETE",
      params: { storeId, date },
    }),

  // Income
  createIncome: (payload: CreateStoreIncomePayload) =>
    apiRequest<StoreIncome>("/store/income", { method: "POST", body: payload }),
  listIncome: (storeId: string, prefix?: string, opts: { category?: string; limit?: number; lastKey?: string } = {}) =>
    apiRequest<{ items: StoreIncome[]; nextKey?: string; totalAmount?: number; totalCount?: number }>("/store/income", {
      params: {
        storeId,
        ...(prefix ? { prefix } : {}),
        ...(opts.category ? { category: opts.category } : {}),
        ...(opts.limit !== undefined ? { limit: String(opts.limit) } : {}),
        ...(opts.lastKey ? { lastKey: opts.lastKey } : {}),
      },
    }),
  async exportIncome(storeId: string, prefix?: string, opts: { category?: string } = {}): Promise<StoreIncome[]> {
    const all: StoreIncome[] = [];
    let lastKey: string | undefined;
    do {
      const res = await storeApi.listIncome(storeId, prefix, { ...opts, limit: 200, lastKey });
      all.push(...res.items);
      lastKey = res.nextKey;
    } while (lastKey);
    return all;
  },
  updateIncome: (incomeId: string, payload: UpdateStoreIncomePayload) =>
    apiRequest<StoreIncome>(`/store/income/${incomeId}`, {
      method: "PATCH",
      body: payload,
      params: { storeId: payload.storeId },
    }),
  deleteIncome: (incomeId: string, storeId: string, date: string) =>
    apiRequest<{ deleted: boolean }>(`/store/income/${incomeId}`, {
      method: "DELETE",
      params: { storeId, date },
    }),

  // Config
  getConfig: (storeId: string) =>
    apiRequest<{ config: StoreConfig | null }>("/store/config", { params: { storeId } }),
  saveConfig: (payload: SaveStoreConfigPayload) =>
    apiRequest<{ config: StoreConfig }>("/store/config", { method: "PUT", body: payload }),
};
